import { useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";

import useApi from "../../hooks/useApi";
import Loader from "../Loader";

import { ReactComponent as GoogleIcon } from "../../static/images/google.svg";

import "./style.scss";

export const GoogleLoginBtn = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryParams = Object.fromEntries([...params]);
  const { pathname, state } = useLocation();
  const { post, put, loading } = useApi();

  const [googleLoading, setGoogleLoading] = useState(false);

  const tryLogin = async (data) => {
    try {
      const loginResp = await post("/User/external-login", {
        email: data.email,
        provider: data.provider,
        providerKey: data.providerKey,
        accessToken: data.accessToken,
        profileUrl: data.profileUrl,
      });

      if (loginResp.isAuthorized) {
        localStorage.setItem("ps10005000", JSON.stringify(loginResp.token));
        localStorage.setItem(
          "ps10004000",
          JSON.stringify(loginResp.refreshToken),
        );
        return { isLoggedIn: true };
      } else {
        return { isLoggedIn: false };
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleManageUser = async (userInfo, accessToken) => {
    const isProviderRegiterPage = pathname === "/register";
    const isConsumerRegiterPage = pathname === "/signUp";
    const { email, id, family_name, given_name, picture } = userInfo;
    const redirectPathAfterLogin = state?.from?.pathname || "/sessions";

    const finalData = {
      email,
      name: given_name,
      surname: family_name,
      accessToken,
      providerKey: id,
      profileUrl: picture,
      provider: "Google",
      pricePlan: queryParams.pricePlan,
    };

    const loginResponse = await tryLogin(finalData);

    if (loginResponse.isLoggedIn) {
      navigate(redirectPathAfterLogin, { replace: true });
    } else if (isProviderRegiterPage) {
      navigate(`/role-selector?isSSO=true`, {
        state: finalData,
      });
    } else if (isConsumerRegiterPage) {
      if (finalData.email !== queryParams?.email) {
        put("/Invitation/SetEmail", {
          id: queryParams.invitationId,
          email: finalData.email,
        });
      }

      try {
        const registerData = await post("/User/external-register", {
          email: finalData.email,
          provider: finalData.provider,
          providerKey: finalData.providerKey,
          accessToken: finalData.accessToken,
          profileUrl: finalData.profileUrl,
          firstName: finalData.name,
          lastName: finalData.surname,
          phoneNumber: queryParams.phoneNumber || null,
          userRole: "Consumer",
        });

        if (registerData.isAuthorized) {
          localStorage.setItem(
            "ps10005000",
            JSON.stringify(registerData.token),
          );
          localStorage.setItem(
            "ps10004000",
            JSON.stringify(registerData.refreshToken),
          );
          navigate("/sessions");
        }
      } catch (e) {
        console.error(e);
      }
    } else {
      navigate("/?isSSO=true", { state: finalData });
    }
  };

  const fetchGoogleUserInfo = async (accessToken) => {
    try {
      setGoogleLoading(true);
      const response = await fetch(
        `https://www.googleapis.com/oauth2/v2/userinfo?access_token=${accessToken}`,
      );
      const userInfo = await response.json();
      handleManageUser(userInfo, accessToken);
    } catch (error) {
      console.error("Error fetching Google user info:", error);
    } finally {
      setGoogleLoading(false);
    }
  };

  const login = useGoogleLogin({
    onSuccess: (tokenResponse) => {
      fetchGoogleUserInfo(tokenResponse.access_token);
    },
    onError: () => console.error("Google Login Failed"),
  });

  return (
    <>
      {(loading || googleLoading) && <Loader />}
      <button onClick={login} className="google-login-btn">
        <GoogleIcon />
        <span>{t("continue_google")}</span>
      </button>
      <div id="google-login-hidden"></div>
    </>
  );
};
